/**
 * Copies text to the clipboard, in the places where the modern API is missing.
 *
 * `navigator.clipboard` only exists in a secure context — HTTPS or localhost.
 * Sharing a session over the local network means everybody is on plain
 * `http://192.168.x.x`, where the API is simply not there, so the old
 * `execCommand` route stays as the fallback.
 *
 * Returns false when both routes fail; callers should then show the value for
 * the user to copy by hand.
 */
export async function copyText(value: string): Promise<boolean> {
  if (typeof navigator.clipboard?.writeText === 'function') {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {
      // Permission denied or a non-focused document — try the fallback.
    }
  }

  return copyWithExecCommand(value);
}

function copyWithExecCommand(value: string): boolean {
  const textarea = document.createElement('textarea');
  textarea.value = value;
  // Keep it out of sight and out of the layout, but still selectable.
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '0';
  textarea.style.left = '0';
  textarea.style.opacity = '0';
  textarea.style.pointerEvents = 'none';

  document.body.appendChild(textarea);
  try {
    textarea.select();
    textarea.setSelectionRange(0, value.length);
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
}

/**
 * True when the page is open on `localhost`, which means an invite link built
 * from this origin only works on this very machine.
 */
export function isLocalhostOrigin(): boolean {
  return ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);
}
